/**
 * Desktop (OS) notifications for incoming chat messages — the Discord/Teams
 * style toast that appears on the side of the screen when the app is in the
 * background. Tauri-only: every entry point is guarded by `isTauri()` so the
 * web build imports nothing and no-ops.
 *
 * Clicking a toast brings the window forward and opens the conversation it
 * came from. On Windows this goes through our own `show_chat_notification`
 * Rust command (WinRT `Activated` callback → `chat-notification-clicked`
 * event), because the notification plugin's `onAction` is mobile-only and
 * toast clicks would otherwise never reach us. Elsewhere the plugin path is
 * used and the click→channel mapping is best-effort: platforms that don't
 * deliver a click event still show the toast and activate the app — it just
 * may not deep-jump to the exact channel.
 */
import { isTauri } from './tauri';
import { initials, avatarColor } from '../components/chatHelpers';
import { useChatStore } from '../store/chatStore';
import { useUiModalStore } from '../store/uiModalStore';
import { useExtensionStore } from '../extensions/store';
import { addSessionEndedListener } from '../services/channelService';

let permissionState = null; // 'granted' | 'denied' | null (unknown/not asked)
let actionListenerReady = false;
const pendingByNotifId = new Map(); // OS notification id -> channelId
const iconPathBySeed = new Map(); // avatar seed -> absolute PNG path on disk
let nextNotifId = 1;

// pendingByNotifId is the one map here that's session-derived (a channelId
// belongs to whoever was signed in when the toast fired) — permissionState/
// actionListenerReady/iconPathBySeed/nextNotifId are not account-scoped and
// are left alone. Without this, a lingering OS-level toast from a departed
// account, clicked after switching accounts on the same device, resolves to
// that departed account's channelId via ensureActionListener's lookup above.
// Had no reset path in production code at all; registered once,
// unconditionally, at module load.
addSessionEndedListener(() => {
  pendingByNotifId.clear();
});

// Test-only accessors so sessionLifecycleNoResidue.test.js can populate and
// observe this holder directly, without mocking the full Tauri notification
// plugin/isTauri() pipeline just to get an entry into the map. Not used by
// any production code path.
export function _setPendingNotifIdForTests(id, channelId) {
  pendingByNotifId.set(id, channelId);
}
export function _hasPendingNotifIdForTests(id) {
  return pendingByNotifId.has(id);
}

/**
 * Render the same initials-on-colour avatar the chat sidebar shows into a PNG
 * (there's no stored avatar image — they're generated from initials + a hashed
 * colour). Returns a data URL, or null if canvas is unavailable.
 */
function renderAvatarDataUrl(name, seed) {
  try {
    const size = 128;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = avatarColor(seed);
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.font = '600 54px system-ui, -apple-system, "Segoe UI", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(initials(name), size / 2, size / 2 + 2);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}

/**
 * Produce an absolute file path to the sender's avatar PNG (the OS toast needs
 * a real file, not a data URL). Cached per seed. Returns null on any failure so
 * the notification can still fire without an icon.
 */
async function avatarIconPath(name, seed) {
  const key = String(seed ?? name ?? '');
  if (!key) return null;
  if (iconPathBySeed.has(key)) return iconPathBySeed.get(key);
  try {
    const dataUrl = renderAvatarDataUrl(name, seed);
    if (!dataUrl) return null;
    const base64Png = dataUrl.slice(dataUrl.indexOf(',') + 1);
    const { invoke } = await import('@tauri-apps/api/core');
    const path = await invoke('save_notification_icon', { key, base64Png });
    iconPathBySeed.set(key, path);
    return path;
  } catch {
    return null;
  }
}

/** Lazily load the plugin only inside Tauri (keeps it out of the web bundle). */
async function loadPlugin() {
  if (!isTauri()) return null;
  try {
    return await import('@tauri-apps/plugin-notification');
  } catch {
    return null;
  }
}

/**
 * Ask for notification permission once (macOS prompts; Windows is implicit).
 * Safe to call repeatedly — caches the result. Returns true if granted.
 */
export async function ensureNotificationPermission() {
  if (permissionState === 'granted') return true;
  if (permissionState === 'denied') return false;
  const mod = await loadPlugin();
  if (!mod) return false;
  try {
    let granted = await mod.isPermissionGranted();
    if (!granted) {
      const res = await mod.requestPermission();
      granted = res === 'granted';
    }
    permissionState = granted ? 'granted' : 'denied';
    return granted;
  } catch {
    return false;
  }
}

/** Bring the main window to the foreground. */
async function focusWindow() {
  if (!isTauri()) return;
  try {
    const { getCurrentWindow } = await import('@tauri-apps/api/window');
    const win = getCurrentWindow();
    await win.unminimize().catch(() => {});
    await win.show().catch(() => {});
    await win.setFocus().catch(() => {});
  } catch {
    /* ignore */
  }
}

/** Focus the window and open the chat panel on a specific channel. */
function openChatChannel(channelId) {
  focusWindow();
  try {
    useExtensionStore.getState().activatePanel('chat.panel');
    useUiModalStore.getState().setActiveTab('chat.panel');
    const s = useChatStore.getState();
    s.setActiveChannel(channelId);
    s.clearUnread(channelId);
  } catch {
    /* store shape changed — window focus already happened */
  }
}

/** Windows: toasts go through our native command instead of the plugin. */
function isWindows() {
  return typeof navigator !== 'undefined' && navigator.userAgent.includes('Windows');
}

let windowsClickListenerReady = false;

/**
 * Listen (once) for the Rust-side `chat-notification-clicked` event fired by
 * the WinRT toast's Activated callback, carrying the channel id.
 */
async function ensureWindowsClickListener() {
  if (windowsClickListenerReady) return;
  windowsClickListenerReady = true;
  try {
    const { listen } = await import('@tauri-apps/api/event');
    await listen('chat-notification-clicked', (event) => {
      const channelId = event?.payload;
      if (channelId != null) openChatChannel(channelId);
      else focusWindow();
    });
  } catch {
    windowsClickListenerReady = false;
  }
}

/**
 * Windows-native toast via the `show_chat_notification` command. Returns true
 * if it was shown (caller should not fall back to the plugin path).
 */
async function showWindowsNotification({ title, body, channelId, iconName, iconSeed }) {
  try {
    await ensureWindowsClickListener();
    const icon = await avatarIconPath(iconName, iconSeed);
    const { invoke } = await import('@tauri-apps/api/core');
    await invoke('show_chat_notification', {
      title,
      body,
      icon: icon || null,
      channelId: channelId ?? null,
    });
    return true;
  } catch {
    return false; // older backend or WinRT failure — plugin path still shows a toast
  }
}

/** Register the click/action listener once, mapping notification id → channel. */
async function ensureActionListener(mod) {
  if (actionListenerReady) return;
  actionListenerReady = true;
  try {
    await mod.onAction((notification) => {
      const chId = pendingByNotifId.get(notification?.id);
      if (chId != null) {
        pendingByNotifId.delete(notification.id);
        openChatChannel(chId);
      } else {
        focusWindow();
      }
    });
  } catch {
    // Platform doesn't support onAction — toast still shows, click still
    // activates the app via OS default; we just can't deep-jump.
    actionListenerReady = false;
  }
}

/**
 * Show a desktop notification for an incoming message. Caller decides *whether*
 * to notify (e.g. only when the app is unfocused); this just displays it.
 */
export async function showChatNotification({ title, body, channelId, iconName, iconSeed }) {
  if (!isTauri()) return;
  if (isWindows()) {
    const shown = await showWindowsNotification({ title, body, channelId, iconName, iconSeed });
    if (shown) return;
  }
  const mod = await loadPlugin();
  if (!mod) return;
  const granted = await ensureNotificationPermission();
  if (!granted) return;
  await ensureActionListener(mod);
  // Best-effort avatar (initials PNG). Falls back to the app icon if it fails —
  // and on some platforms (notably macOS) the OS ignores per-notification icons
  // and shows the app icon regardless; on Windows it renders like Teams.
  const icon = await avatarIconPath(iconName, iconSeed);
  const id = nextNotifId++;
  if (nextNotifId > 2_000_000_000) nextNotifId = 1;
  if (channelId != null) pendingByNotifId.set(id, channelId);
  try {
    mod.sendNotification(icon ? { id, title, body, icon } : { id, title, body });
  } catch {
    if (channelId != null) pendingByNotifId.delete(id);
  }
}
